import { useState, useEffect, useMemo } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaPlusCircle, FaEdit, FaLightbulb, FaPhone, FaEnvelope, FaCheckCircle, FaCopy, FaSearch, FaExclamationTriangle } from 'react-icons/fa'
import { bookingService } from '../services'
import { getServiceContact } from '../services/serviceContact'
import { Card, Button, Input, Select, PageHeader, PhoneTailVerifyModal, WecomContactCard, Modal } from '../components/common'
import { useAuth } from '../hooks/useAuth'
import { siteConfig } from '../config/site'

const shippingMethods = [
  { value: '全部', label: '全部方式' },
  { value: '国际空派', label: '国际空派' },
  { value: '国际海派', label: '国际海派' },
  { value: '国际快递', label: '国际快递' },
  { value: '国际小包', label: '国际小包' },
]

const serviceProviderOptions = [{ value: 'CFGL出运', label: 'CFGL出运' }]

export default function ConsolidationPage() {
  const breadcrumb = useBreadcrumb()
  const { isAuthenticated } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittedBooking, setSubmittedBooking] = useState(null)
  const [showOverlay, setShowOverlay] = useState(false)
  const [queryCode, setQueryCode] = useState('')
  const [queryResult, setQueryResult] = useState(null)
  const [isQuerying, setIsQuerying] = useState(false)
  // 单号查询的身份校验（弹窗为 common/PhoneTailVerifyModal，与我的报名/我的订单共用）
  const [verifyModal, setVerifyModal] = useState({ visible: false, code: '' })
  const [itemTypeNames, setItemTypeNames] = useState({})
  const [sensitiveMessage, setSensitiveMessage] = useState('')
  const [destinationCountries, setDestinationCountries] = useState([])
  const [provinceOptions, setProvinceOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [districtOptions, setDistrictOptions] = useState([])
  const [streetOptions, setStreetOptions] = useState([])

  // 本人报名列表：只用来判断「查的是不是自己的单」—— 是自己的就不必再校验手机后四位，
  // 并且列表里已带着要展示的字段，无需再发一次详情请求。
  // 游客不请求（/list 需登录，会被 api.js 判成未登录并踢去登录页）。
  // queryKey 与「我的报名」(CargoPage) 一致，两页共用同一份缓存，来回切不重复请求。
  const { data: myBookings } = useQuery({
    queryKey: ['bookings', 'my-list'],
    queryFn: () => bookingService.getBookings({ size: 100 }),
    enabled: isAuthenticated,
  })
  const myBookingRecords = Array.isArray(myBookings) ? myBookings : (myBookings?.records || [])

  // 企微引导配置。只在报名成功页要用，所以等 submittedBooking 出现才请求。
  // 未配置 / 请求失败时 service 层返回 null，卡片整块不渲染 —— 这一块是锦上添花，
  // 绝不能让它把「记住报名编号」这件正事挤掉或拖慢。
  const { data: wecomContact } = useQuery({
    queryKey: ['service-contact', 'booking_success'],
    queryFn: () => getServiceContact(),
    enabled: Boolean(submittedBooking),
    staleTime: 5 * 60 * 1000,
  })

  const { register, handleSubmit, reset, watch, setValue, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues: {
      quantity: 1,
      pickupCountry: '中国',
      serviceProvider: 'CFGL出运',
    },
  })

  const watchItemType = watch('itemType')
  const watchItemName = watch('itemName')
  const watchOtherItemName = watch('otherItemName')
  // const watchContactName = watch('contactName')
  // const watchContactPhone = watch('contactPhone')
  const watchDestinationCountry = watch('destinationCountry')
  // const watchDestinationCity = watch('destinationCity')
  // const watchDeliveryAddress = watch('deliveryAddress')
  // const watchPickupDetailAddress = watch('pickupDetailAddress')
  const watchPickupRequired = watch('pickupRequired')
  const watchPickupProvince = watch('pickupProvince')
  const watchPickupCity = watch('pickupCity')
  const watchPickupDistrict = watch('pickupDistrict')
  const watchLength = watch('length')
  const watchWidth = watch('width')
  const watchHeight = watch('height')

  const itemTypeOptions = useMemo(() => {
    const fromApi = Object.keys(itemTypeNames).map(key => ({ value: key, label: key }))
    return [...fromApi, { value: '其他', label: '其他' }]
  }, [itemTypeNames])

  const itemNameOptions = useMemo(() => {
    if (!watchItemType || watchItemType === '其他') return []
    return (itemTypeNames[watchItemType] || []).map(name => ({ value: name, label: name }))
  }, [watchItemType, itemTypeNames])

  // const step1Done = !!(watchContactName && watchContactPhone)
  // const step2Done = !!(watchDestinationCountry && watchDestinationCity && watchDeliveryAddress && watchPickupRequired && (Number(watchPickupRequired) !== 1 || (watchPickupProvince && watchPickupCity && watchPickupDistrict && watchPickupDetailAddress)))
  // const step3Done = !!(watchItemType && (watchItemType === '其他' ? watchOtherItemName : watchItemName))

  const itemTypeDisabled = !watchDestinationCountry
  const itemTypePlaceholder = itemTypeDisabled ? '请先选择收货国家' : '请选择物品类型'
  const itemNameDisabled = !watchDestinationCountry || !watchItemType || watchItemType === '其他'
  const itemNamePlaceholder = !watchDestinationCountry
    ? '请先选择收货国家'
    : (!watchItemType ? '请先选择物品类型' : '请选择物品名称')

  const pickupCityDisabled = Number(watchPickupRequired) !== 1 || !watchPickupProvince
  const pickupDistrictDisabled = Number(watchPickupRequired) !== 1 || !watchPickupCity
  const pickupStreetDisabled = Number(watchPickupRequired) !== 1 || !watchPickupDistrict

  useEffect(() => {
    setValue('serviceProvider', 'CFGL出运')
  }, [setValue])

  useEffect(() => {
    if (!watchDestinationCountry) {
      setItemTypeNames({})
      setValue('itemType', undefined)
      setValue('itemName', undefined)
      setValue('otherItemName', undefined)
      return
    }
    bookingService.getSensitiveGoodsOptions(watchDestinationCountry)
      .then((res) => {
        const options = res?.itemTypeNames || {}
        setItemTypeNames(options)
        setValue('itemType', undefined)
        setValue('itemName', undefined)
        setValue('otherItemName', undefined)
      })
      .catch(() => {
        setItemTypeNames({})
      })
  }, [watchDestinationCountry, setValue])

  useEffect(() => {
    bookingService.getDestinationCountries()
      .then((res) => {
        if (Array.isArray(res) && res.length > 0) {
          if (typeof res[0] === 'string') {
            setDestinationCountries(res.map(name => ({ value: name, label: name })))
          } else {
            setDestinationCountries(res.map(item => {
              const zh = item.country_name || ''
              const en = item.country_name_en || ''
              const display = zh && en ? `${zh} / ${en}` : zh
              return { value: display, label: display }
            }).filter(v => v.value))
          }
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    bookingService.getRegionChildren('00').then((res) => {
      const nextOptions = (res || []).map(item => ({ value: item.code, label: item.name }))
      setProvinceOptions(nextOptions)
      // 不自动选中省份，保持placeholder
      // if (Number(watchPickupRequired) === 1 && !watchPickupProvince && nextOptions.length > 0) {
      //   setValue('pickupProvince', nextOptions[0].value)
      // }
    }).catch(() => setProvinceOptions([]))
  }, [setValue, watchPickupProvince, watchPickupRequired])

  useEffect(() => {
    if (!watchPickupProvince) {
      setCityOptions([])
      setDistrictOptions([])
      setStreetOptions([])
      return
    }
    setValue('pickupCity', undefined)
    setValue('pickupDistrict', undefined)
    setValue('pickupStreet', undefined)
    bookingService.getRegionChildren(watchPickupProvince).then((res) => {
      let nextCities = (res || []).map(item => ({ value: item.code, label: item.name }))
      if (nextCities.length === 0) {
        nextCities = [{ value: watchPickupProvince, label: getOptionLabel(provinceOptions, watchPickupProvince) }]
      }
      setCityOptions(nextCities)
      setDistrictOptions([])
      setStreetOptions([])
      // 不自动选中城市，保持placeholder
      // if (nextCities.length > 0) {
      //   setValue('pickupCity', nextCities[0].value)
      // }
    }).catch(() => setCityOptions([]))
  }, [watchPickupProvince, setValue, provinceOptions])

  useEffect(() => {
    if (!watchPickupCity) {
      setDistrictOptions([])
      setStreetOptions([])
      return
    }
    setValue('pickupDistrict', undefined)
    setValue('pickupStreet', undefined)
    bookingService.getRegionChildren(watchPickupCity).then((res) => {
      const nextDistricts = (res || []).map(item => ({ value: item.code, label: item.name }))
      setDistrictOptions(nextDistricts)
      setStreetOptions([])
      // 不自动选中区县，保持placeholder
      // if (nextDistricts.length > 0) {
      //   setValue('pickupDistrict', nextDistricts[0].value)
      // }
    }).catch(() => setDistrictOptions([]))
  }, [watchPickupCity, setValue])

  useEffect(() => {
    if (!watchPickupDistrict) {
      setStreetOptions([])
      return
    }
    setValue('pickupStreet', undefined)
    bookingService.getRegionChildren(watchPickupDistrict).then((res) => {
      const nextStreets = (res || []).map(item => ({ value: item.code, label: item.name }))
      setStreetOptions(nextStreets)
      // 不自动选中街道，保持placeholder
      // if (nextStreets.length > 0) {
      //   setValue('pickupStreet', nextStreets[0].value)
      // }
    }).catch(() => setStreetOptions([]))
  }, [watchPickupDistrict, setValue])

  useEffect(() => {
    if (!watchLength || !watchWidth || !watchHeight) {
      setValue('volume', '')
      return
    }
    const l = parseFloat(watchLength)
    const w = parseFloat(watchWidth)
    const h = parseFloat(watchHeight)
    if (Number.isNaN(l) || Number.isNaN(w) || Number.isNaN(h)) {
      setValue('volume', '')
      return
    }
    if (l < 0 || w < 0 || h < 0) {
      setValue('volume', '')
      return
    }
    const v = (l * w * h) / 6000
    setValue('volume', v.toFixed(4))
  }, [watchLength, watchWidth, watchHeight, setValue])

  const getOptionLabel = (options, value) => {
    const found = options.find(opt => opt.value === value)
    return found ? found.label : value
  }

  useEffect(() => {
    if (!watchDestinationCountry || !watchItemType) {
      setSensitiveMessage('')
      return
    }

    const selectedItemName = watchItemType === '其他' ? watchOtherItemName : watchItemName
    if (!selectedItemName) {
      setSensitiveMessage('')
      return
    }

    bookingService.checkSensitiveGoods({
      destinationCountry: watchDestinationCountry,
      itemType: watchItemType,
      itemName: selectedItemName,
    }).then((res) => {
      setSensitiveMessage(res?.sensitive ? res?.message : '')
    }).catch(() => {
      setSensitiveMessage('')
    })
  }, [watchDestinationCountry, watchItemType, watchItemName, watchOtherItemName])

  // Create booking mutation
  const createMutation = useMutation({
    mutationFn: bookingService.createBooking,
    onSuccess: (data) => {
      toast.success('报名提交成功！')
      setSubmittedBooking(data)
      setShowOverlay(true)
      setSensitiveMessage('')
      reset()
      // 表单很长，提交时用户往往已滚到底部；成功页比表单短得多，
      // 不回顶的话点完「确认」看到的是页脚，报名编号在屏幕外（移动端尤其明显）。
      window.scrollTo({ top: 0 })
    },
    onError: (error) => {
      toast.error(error.message || '提交失败，请稍后重试')
    },
  })

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      const selectedItemName = data.itemType === '其他' ? data.otherItemName : data.itemName
      if (!selectedItemName) {
        setError(data.itemType === '其他' ? 'otherItemName' : 'itemName', {
          type: 'required',
          message: '请输入物品名称',
        })
        return
      }
      clearErrors(['itemName', 'otherItemName'])

      const pickupAddress = Number(data.pickupRequired) === 1
        ? [
          getOptionLabel(provinceOptions, data.pickupProvince),
          getOptionLabel(cityOptions, data.pickupCity),
          getOptionLabel(districtOptions, data.pickupDistrict),
          getOptionLabel(streetOptions, data.pickupStreet),
          data.pickupDetailAddress,
        ]
            .filter(Boolean)
            .join(' ')
        : ''

      // 计算体积
      let volume = data.volume
      if (!volume && data.length && data.width && data.height) {
        volume = (parseFloat(data.length) * parseFloat(data.width) * parseFloat(data.height)) / 1000000
      }

      const lengthVal = data.length ? parseFloat(data.length) : null
      const widthVal = data.width ? parseFloat(data.width) : null
      const heightVal = data.height ? parseFloat(data.height) : null
      const volumeVal = volume ? parseFloat(volume) : null

      if ((lengthVal !== null && lengthVal < 0) || (widthVal !== null && widthVal < 0) || (heightVal !== null && heightVal < 0) || (volumeVal !== null && volumeVal < 0)) {
        toast.error('长、宽、高及体积比不能为负值')
        return
      }

      await createMutation.mutateAsync({
        contactName: data.contactName,
        contactPhone: data.contactPhone,
        cargoName: selectedItemName,
        itemType: data.itemType,
        itemName: selectedItemName,
        cargoDescription: data.cargoDescription,
        weight: parseFloat(data.weight),
        volume: volumeVal,
        length: lengthVal,
        width: widthVal,
        height: heightVal,
        quantity: parseInt(data.quantity) || 1,
        declaredValue: parseFloat(data.declaredValue),
        pickupRequired: parseInt(data.pickupRequired, 10),
        pickupAddress,
        pickupCountry: data.pickupCountry,
        pickupProvince: getOptionLabel(provinceOptions, data.pickupProvince),
        pickupCity: getOptionLabel(cityOptions, data.pickupCity),
        pickupDistrict: getOptionLabel(districtOptions, data.pickupDistrict),
        pickupStreet: getOptionLabel(streetOptions, data.pickupStreet),
        pickupDetailAddress: data.pickupDetailAddress,
        destinationCountry: data.destinationCountry,
        destinationCity: data.destinationCity,
        deliveryAddress: data.deliveryAddress,
        shippingMethod: data.shippingMethod,
        logisticsOption: data.shippingMethod,
        serviceProvider: data.serviceProvider,
        needDelivery: parseInt(data.needDelivery, 10),
        remarks: data.remarks,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // 点击「查询」：
  //   查自己的单（已登录且单号在本人列表里）→ 直接展示，不校验手机后四位；
  //   其余情况（游客，或查别人的单）→ 拉起校验弹窗，真正的查询在 handleVerify 里。
  // 开头先清掉上一次的结果 —— 否则输入框清空后再点查询，旧结果会继续挂在页面上，
  // 看起来像是「空单号也能查出东西」。
  const handleQueryBooking = () => {
    setQueryResult(null)
    const code = queryCode.trim()
    if (!code) {
      toast.error('请输入报名编号')
      return
    }
    if (isAuthenticated) {
      const own = myBookingRecords.find(
        (b) => String(b.booking_code || '').toLowerCase() === code.toLowerCase()
      )
      if (own) {
        setQueryResult(own)
        return
      }
    }
    setVerifyModal({ visible: true, code })
  }

  // 弹窗提交：校验「报名编号 + 手机号后四位」，通过后才返回该报名
  const handleVerify = async (phoneTail) => {
    setIsQuerying(true)
    try {
      const result = await bookingService.verifyBookingByCode(verifyModal.code, phoneTail)
      setQueryResult(result)
      setVerifyModal({ visible: false, code: '' })
      toast.success('校验成功')
    } catch (error) {
      toast.error(error.message || '校验失败')
    } finally {
      setIsQuerying(false)
    }
  }

  // 复制报名编号
  const copyBookingCode = (code) => {
    navigator.clipboard.writeText(code)
    toast.success('报名编号已复制')
  }

  // 提交成功后的展示
  if (submittedBooking) {
    // 二选一：运营没传二维码时说「添加企业微信」就是空承诺。都不写具体时长，实际做不到必达。
    const successSubtitle = wecomContact
      ? '您的集运报名已提交，请添加下方企业微信沟通报价'
      : '您的集运报名已提交，客服会尽快与您联系'

    return (
      <div>
        <PageHeader
          icon={FaCheckCircle}
          title="报名成功"
          subtitle={successSubtitle}
          breadcrumb={breadcrumb}
        />

        {/* 遮罩提示 */}
        <Modal open={showOverlay} onClose={() => setShowOverlay(false)} closable={false}>
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaExclamationTriangle className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-fluid-xl font-bold text-gray-900 mb-3">重要提示</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              稍后请保存报名编号，丢失后只能联系客服处理
            </p>
            <Button size="lg" onClick={() => setShowOverlay(false)} className="w-full">
              确认
            </Button>
          </div>
        </Modal>

        <div className="container py-8">
          <Card className="max-w-2xl mx-auto">
            <Card.Body className="text-center py-12">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />
              </div>

              <h3 className="text-fluid-2xl font-bold text-gray-900 mb-2">报名提交成功！</h3>
              <p className="text-fluid-base text-gray-600 mb-8">请保存您的报名编号，以便后续查询进度</p>

              {/* 编号是本页第一要务。原先 text-fluid-3xl(30px) 等宽字体要 214px，
                  320px 屏容器只给 128px，「CB-2026-00020」被折成三行。
                  小屏降一档并 whitespace-nowrap 保证整体一行。 */}
              <div className="bg-gray-50 rounded-lg px-4 py-6 sm:p-6 mb-8">
                <p className="text-sm text-gray-500 mb-2">报名编号</p>
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <span className="text-fluid-3xl font-mono font-bold text-primary-600 whitespace-nowrap">
                    {submittedBooking.booking_code}
                  </span>
                  <button
                    onClick={() => copyBookingCode(submittedBooking.booking_code)}
                    className="flex items-center justify-center w-10 h-10 shrink-0 hover:bg-gray-200 rounded-lg transition-colors"
                    title="复制编号"
                    aria-label="复制报名编号"
                  >
                    <FaCopy className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/*
                企微引导紧跟编号，摘要退到最后。三块的优先级是
                编号（要记住）> 加企微（真正的下一步，决定能不能拿到报价）> 摘要（5 秒前刚填，只是确认）。
                原先摘要插在中间，移动端 4 张卡竖排占 352px，把企微整块顶出首屏。
              */}
              <WecomContactCard contact={wecomContact} />

              {/* 固定两列，不用 auto-fit：最小列宽 10rem 在小屏 326px 容器里放不下两列，
                  会塌成 1 列变成 4 整行（352px）。两列在 320px 也成立，只是长值折行。 */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 text-left mb-8">
                {[
                  ['物品名称', submittedBooking.cargo_name],
                  ['重量', submittedBooking.weight ? `${submittedBooking.weight} KG` : null],
                  ['目的地', submittedBooking.destination_country],
                  ['运货方式', submittedBooking.logistics_option],
                ].map(([label, value]) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <p className="text-fluid-xs sm:text-sm text-gray-500 mb-1">{label}</p>
                    <p className="text-fluid-sm sm:text-base font-medium break-words">{value || '-'}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => setSubmittedBooking(null)} className="w-full sm:w-auto">
                  继续报名
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        icon={FaPlusCircle}
        title="集运报名"
        subtitle="一键提交需求，专人对接，全程追踪"
        breadcrumb={breadcrumb}
      />

      <div className="container py-8">
        {/* 报名编号查询 */}
        <Card className="mb-8">
          <Card.Body>
            {/* 小屏竖排：并排时输入框只剩 100px，placeholder 只能显示「输入报名编」 */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FaSearch className="text-gray-400 text-xl shrink-0" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="报名编号 CB-2026-00001"
                  value={queryCode}
                  onChange={(e) => {
                    setQueryCode(e.target.value)
                    // 结果属于上一次查询的那个单号，编号一改就不再对应，立即清掉，
                    // 避免「输入框里是新单号、下面还挂着旧单号的结果」
                    if (queryResult) setQueryResult(null)
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && handleQueryBooking()}
                />
              </div>
              <Button onClick={handleQueryBooking} className="w-full sm:w-auto shrink-0">
                查询
              </Button>
            </div>

            <p className="text-sm text-gray-500 mt-3">
              {isAuthenticated
                ? '查询自己的报名只需编号；查询他人报名需再校验其报名时预留的手机号后四位。'
                : '需凭「报名编号 + 报名时预留的手机号后四位」查询，仅凭编号无法查看他人报名。'}
            </p>

            {queryResult && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-fluid-sm gap-4">
                  <div>
                    <p className="text-sm text-gray-500">报名编号</p>
                    <p className="font-medium">{queryResult.booking_code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">货物名称</p>
                    <p className="font-medium">{queryResult.cargo_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">收货国家</p>
                    {/* 原先死写 max-w-[250px]，在 320px 屏上比可用宽度还大、等于没截断 */}
                    <p
                      className="font-medium truncate"
                      title={queryResult.destination_country}
                    >
                      {queryResult.destination_country}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">状态</p>
                    <span className={`inline-block px-2 py-1 rounded text-sm ${
                      queryResult.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      (queryResult.status === 'confirmed' || queryResult.status === 'in_transit') ? 'bg-blue-100 text-blue-800' :
                      (queryResult.status === 'completed' || queryResult.status === 'delivered') ? 'bg-green-100 text-green-800' :
                      queryResult.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {queryResult.status === 'pending' ? '报价中' :
                       (queryResult.status === 'confirmed' || queryResult.status === 'in_transit') ? '已报价' :
                       queryResult.status === 'rejected' ? '已拒绝' :
                       (queryResult.status === 'completed' || queryResult.status === 'delivered') ? '已完成' :
                       queryResult.status === 'cancelled' ? '已取消' :
                       queryResult.status}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card>
              <Card.Header>
                <h4 className="font-semibold flex items-center gap-2">
                  <FaEdit className="text-primary-500" />
                  填写报名信息
                </h4>
                <p className="text-sm text-gray-500 mt-1">无需登录即可报名，提交后客服将联系您</p>
              </Card.Header>
              <Card.Body>
                {/* <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="grid grid-cols-5 gap-2 text-sm">
                    <div className={`text-center py-1 rounded ${step1Done ? 'bg-green-100 text-green-700' : 'bg-white text-gray-600'}`}>1 联系信息</div>
                    <div className={`text-center py-1 rounded ${step2Done ? 'bg-green-100 text-green-700' : 'bg-white text-gray-600'}`}>2 地址信息</div>
                    <div className={`text-center py-1 rounded ${step3Done ? 'bg-green-100 text-green-700' : 'bg-white text-gray-600'}`}>3 物品信息</div>
                    <div className={`text-center py-1 rounded ${step3Done ? 'bg-white text-gray-600' : 'bg-gray-100 text-gray-400'}`}>4 物流选择</div>
                    <div className={`text-center py-1 rounded ${step3Done ? 'bg-white text-gray-600' : 'bg-gray-100 text-gray-400'}`}>5 其他信息</div>
                  </div>
                </div> */}

                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
                  {/* Section 1: Contact Info */}
                  <div style={{ order: 1 }}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-7 h-7 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">1</span>
                      <h5 className="font-medium">联系信息</h5>
                    </div>
                    <div className="pl-0 sm:pl-10 space-y-4">
                      <div className="grid grid-cols-fluid-md gap-4">
                        <Input
                          label="姓名"
                          placeholder="请输入您的姓名"
                          required
                          error={errors.contactName?.message}
                          {...register('contactName', { required: '请输入姓名' })}
                        />
                        <Input
                          label="联系电话"
                          placeholder="请输入手机号码"
                          required
                          error={errors.contactPhone?.message}
                          {...register('contactPhone', {
                            required: '请输入联系电话',
                            pattern: {
                              value: /^1[3-9]\d{9}$/,
                              message: '请输入正确的手机号码',
                            },
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Cargo Info */}
                  <div className="pt-6 border-t" style={{ order: 3 }}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-7 h-7 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">3</span>
                      <h5 className="font-medium">物品信息</h5>
                    </div>
                    <div className="pl-0 sm:pl-10 space-y-4">
                      <div className="grid grid-cols-fluid-md gap-4">
                        <Select
                          label="物品类型"
                          required
                          placeholder={itemTypePlaceholder}
                          options={itemTypeOptions}
                          error={errors.itemType?.message}
                          disabled={itemTypeDisabled}
                          {...register('itemType', { required: '请选择物品类型' })}
                        />
                        {watchItemType === '其他' ? (
                          <Input
                            label="物品名称"
                            placeholder="请输入物品名称"
                            required
                            error={errors.otherItemName?.message}
                            {...register('otherItemName', { required: '请输入物品名称' })}
                          />
                        ) : (
                          <Select
                            label="物品名称"
                            required
                            placeholder={itemNamePlaceholder}
                            options={itemNameOptions}
                            error={errors.itemName?.message}
                            disabled={itemNameDisabled}
                            {...register('itemName', { required: '请选择物品名称' })}
                          />
                        )}
                      </div>

                      {sensitiveMessage && (
                        <div className="rounded-lg border border-orange-300 bg-orange-50 p-4 text-orange-800 text-sm flex items-start gap-2">
                          <FaExclamationTriangle className="mt-0.5" />
                          <span>{sensitiveMessage}</span>
                        </div>
                      )}

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <Input
                          label="件数"
                          type="number"
                          min="1"
                          required
                          error={errors.quantity?.message}
                          {...register('quantity', { required: '请输入件数', min: { value: 1, message: '件数至少为1' } })}
                        />
                        <Input
                          label="重量 (KG)"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          required
                          error={errors.weight?.message}
                          {...register('weight', {
                            required: '请输入重量',
                            min: { value: 0.01, message: '重量必须大于0' },
                          })}
                        />
                        <Input
                          label="物品总值"
                          type="number"
                          step="0.01"
                          placeholder="请输入物品总值"
                          required
                          wrapperClassName="col-span-2 sm:col-span-1"
                          error={errors.declaredValue?.message}
                          {...register('declaredValue', {
                            required: '请输入物品总值',
                            min: { value: 0, message: '总值不能小于0' },
                          })}
                        />
                      </div>

                      {/* 原先 grid-cols-2 时每格 84px，placeholder 全截断。小屏改成
                          长/宽/高 一行三格（占位只放「0」），体积比独占一行以放下公式。 */}
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                        <Input
                          label="长 (cm)"
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="0"
                          required
                          error={errors.length?.message}
                          {...register('length', {
                            required: '请输入长度',
                            min: { value: 0, message: '长度不能为负值' },
                          })}
                        />
                        <Input
                          label="宽 (cm)"
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="0"
                          required
                          error={errors.width?.message}
                          {...register('width', {
                            required: '请输入宽度',
                            min: { value: 0, message: '宽度不能为负值' },
                          })}
                        />
                        <Input
                          label="高 (cm)"
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="0"
                          required
                          error={errors.height?.message}
                          {...register('height', {
                            required: '请输入高度',
                            min: { value: 0, message: '高度不能为负值' },
                          })}
                        />
                        <Input
                          label="体积比"
                          type="number"
                          min="0"
                          step="0.0001"
                          placeholder="长*宽*高/6000"
                          disabled
                          wrapperClassName="col-span-3 sm:col-span-1"
                          error={errors.volume?.message}
                          {...register('volume', {
                            min: { value: 0, message: '体积比不能为负值' },
                          })}
                        />
                      </div>
                      <Input
                        label="货物描述（可选）"
                        placeholder="品牌、型号等补充说明"
                        {...register('cargoDescription')}
                      />
                    </div>
                  </div>

                  {/* Section 3: Address Info */}
                  <div className="pt-6 border-t" style={{ order: 2 }}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-7 h-7 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">2</span>
                      <h5 className="font-medium">地址信息</h5>
                    </div>
                    <div className="pl-0 sm:pl-10 space-y-4">
                      <div className="grid grid-cols-fluid-md gap-4">
                        <Select
                          label="发货国家"
                          required
                          options={[{ value: '中国', label: '中国' }]}
                          {...register('pickupCountry', { required: '请选择发货国家' })}
                        />
                        <Select
                          label="上门取件"
                          required
                          placeholder="请选择是否上门取件"
                          options={[{ value: '1', label: '是' }, { value: '0', label: '否' }]}
                          error={errors.pickupRequired?.message}
                          {...register('pickupRequired', { required: '请选择是否上门取件' })}
                          value={watch('pickupRequired') || ''}
                        />
                      </div>

                      {Number(watchPickupRequired) === 1 && (
                        <>
                          <div className="grid grid-cols-fluid-sm gap-4">
                            <Select
                              label="取货省份"
                              required
                              placeholder="请选择省份"
                              options={provinceOptions}
                              error={errors.pickupProvince?.message}
                              {...register('pickupProvince', { required: '请选择省份' })}
                              value={watch('pickupProvince') || ''}
                            />
                            <Select
                              label="取货城市"
                              required
                              placeholder={watchPickupProvince ? '请选择城市' : '请先选择省份'}
                              options={cityOptions}
                              error={errors.pickupCity?.message}
                              disabled={pickupCityDisabled}
                              {...register('pickupCity', { required: '请选择城市' })}
                              value={watch('pickupCity') || ''}
                            />
                            <Select
                              label="取货区域"
                              required
                              placeholder={watchPickupCity ? '请选择区/县' : '请先选择城市'}
                              options={districtOptions}
                              error={errors.pickupDistrict?.message}
                              disabled={pickupDistrictDisabled}
                              {...register('pickupDistrict', { required: '请选择区/县' })}
                              value={watch('pickupDistrict') || ''}
                            />
                            <Select
                              label="取货街道"
                              required
                              placeholder={watchPickupDistrict ? '请选择街道/镇' : '请先选择区/县'}
                              options={streetOptions}
                              error={errors.pickupStreet?.message}
                              disabled={pickupStreetDisabled}
                              {...register('pickupStreet', { required: '请选择街道/镇' })}
                              value={watch('pickupStreet') || ''}
                            />
                          </div>
                          <Input
                            label="取货详细地址"
                            placeholder="请输入详细地址"
                            required
                            error={errors.pickupDetailAddress?.message}
                            {...register('pickupDetailAddress', { required: '请输入取货详细地址' })}
                          />
                        </>
                      )}

                      <div className="grid grid-cols-fluid-md gap-4">
                        <Select
                          label="收货国家"
                          required
                          placeholder="请选择收货国家"
                          options={destinationCountries}
                          error={errors.destinationCountry?.message}
                          {...register('destinationCountry', { required: '请选择目的国家' })}
                          value={watch('destinationCountry') || ''}
                        />
                        <Input
                          label="目的城市"
                          placeholder="请输入目的城市"
                          required
                          error={errors.destinationCity?.message}
                          {...register('destinationCity', { required: '请输入目的城市' })}
                        />
                      </div>

                      <div>
                        <label className="form-label">收货地址<span className="text-red-500 ml-1">*</span></label>
                        <textarea
                          className="form-control"
                          rows={2}
                          placeholder="请输入详细收货地址"
                          {...register('deliveryAddress', { required: '请输入收货地址' })}
                        />
                        {errors.deliveryAddress?.message && <p className="mt-1 text-sm text-red-500">{errors.deliveryAddress.message}</p>}
                      </div>

                      <div className="grid grid-cols-fluid-md gap-4">
                        <Select
                          label="是否需要派送"
                          required
                          placeholder="请选择是否需要派送"
                          options={[{ value: '1', label: '是' }, { value: '0', label: '否' }]}
                          error={errors.needDelivery?.message}
                          {...register('needDelivery', { required: '请选择是否需要派送' })}
                          value={watch('needDelivery') || ''}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Provider & Logistics */}
                  <div className="pt-6 border-t" style={{ order: 4 }}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-7 h-7 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">4</span>
                      <h5 className="font-medium">物流选择</h5>
                    </div>
                    <div className="pl-0 sm:pl-10 space-y-4">
                      <Select
                        label="服务商"
                        required
                        options={serviceProviderOptions}
                        {...register('serviceProvider', { required: '请选择服务商' })}
                      />

                      <Select
                        label="运货方式"
                        required
                        placeholder="请选择运货方式"
                        options={shippingMethods}
                        error={errors.shippingMethod?.message}
                        {...register('shippingMethod', { required: '请选择运货方式' })}
                        value={watch('shippingMethod') || ''}
                      />
                    </div>
                  </div>

                  {/* Section 5: Remarks */}
                  <div className="pt-6 border-t" style={{ order: 5 }}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-7 h-7 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">5</span>
                      <h5 className="font-medium">其他信息</h5>
                    </div>
                    <div className="pl-0 sm:pl-10">
                      <label className="form-label">备注说明</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="特殊需求、注意事项等（可选）"
                        {...register('remarks')}
                      />
                    </div>
                  </div>

                  <div className="pt-6 border-t" style={{ order: 6 }}>
                    <Button
                      type="submit"
                      size="lg"
                      loading={isSubmitting}
                      icon={<FaPlusCircle />}
                    >
                      提交报名
                    </Button>
                    <p className="text-sm text-gray-500 mt-2">
                      提交后将生成报名编号，请保存好以便查询进度
                    </p>
                  </div>
                </form>
              </Card.Body>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tips */}
            <Card>
              <Card.Header>
                <h5 className="font-semibold flex items-center gap-2">
                  <FaLightbulb className="text-yellow-500" />
                  报名须知
                </h5>
              </Card.Header>
              <Card.Body>
                <ul className="space-y-3">
                  {[
                    '无需登录即可报名，方便快捷',
                    '提交后由专属客服与您沟通报价',
                    '货物信息越详细，报价越准确',
                    '请保存好报名编号，以便查询进度',
                    '系统会自动识别敏感货物并提示风险',
                    '若选择“其他”物品类型，请准确填写物品名称',
                  ].map((tip, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-green-500 mt-0.5">✓</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>

            {/* Logistics Options Info */}
            <Card>
              <Card.Header>
                <h5 className="font-semibold">运货方式说明</h5>
              </Card.Header>
              <Card.Body>
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-800">国际空派</p>
                    <p className="text-sm text-blue-600">时效：10-15天，适合轻货、时效要求中等</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-800">国际海派</p>
                    <p className="text-sm text-green-600">时效：20-30天，适合重货、成本敏感型客户</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <p className="font-medium text-yellow-800">国际小包</p>
                    <p className="text-sm text-yellow-600">时效：15-25天，适合小件轻货、低价值货物</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="font-medium text-red-800">国际快递</p>
                    <p className="text-sm text-red-600">时效：3-7天，适合急单、高价值货物</p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Contact */}
            <Card>
              <Card.Body>
                <h5 className="font-semibold flex items-center gap-2 mb-3">
                  <FaPhone className="text-primary-500" />
                  需要帮助？
                </h5>
                <p className="text-sm text-gray-500 mb-4">如有任何疑问，欢迎联系客服</p>
                <div className="space-y-2">
                  <a href={`tel:${siteConfig.servicePhone}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600">
                    <FaPhone className="text-gray-400" />
                    {siteConfig.servicePhone}
                  </a>
                  <a href={`mailto:${siteConfig.contactEmail}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600">
                    <FaEnvelope className="text-gray-400" />
                    {siteConfig.contactEmail}
                  </a>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>

      <PhoneTailVerifyModal
        visible={verifyModal.visible}
        code={verifyModal.code}
        loading={isQuerying}
        onCancel={() => setVerifyModal({ visible: false, code: '' })}
        onConfirm={handleVerify}
      />
    </div>
  )
}
